import React from 'react';
import { Table } from '@brighterion/br-ui';
import { Cell, Row } from 'react-table';
import { handleOnClickEvent, handleKeyEvent } from 'Utils';
import { NumberService, RoleService } from 'Services';
import clsx from 'clsx';
import styles from './index.css';
import TableCell from '../../Cell';
import { ConditionalRowClassNames, OurTableProps } from '../../types';

function RowCell<D extends object = {}>({
    cell,
    row,
    conditionalRowClasses,
    handleEditRow,
    tableName,
    rulesGroupData
}: Pick<OurTableProps<D>, 'conditionalRowClasses'> & {
    cell: Cell<D>;
    row: Row<D>;
    inactiveRowFn?: (row: Row<D>) => boolean;
    handleEditRow?: Function;
    tableName?: string;
    rulesGroupData?: RuleGroup[];
}) {
    const tdClassName = 'td';
    const { isThresholdEditorOnly } = RoleService;
    const isShowEdit =
        !isThresholdEditorOnly &&
        rulesGroupData?.find((group) => group.name === cell?.value)?.adhocGroupId &&
        tableName?.includes('sandbox-global-rules');
    const conditionalStyle = cell.column?.conditionalCellStyle?.(cell) ?? {};
    const cellProps = cell.getCellProps([
        {
            style: {
                maxWidth: `${cell.column.maxWidth}px`,
                ...cell.column?.style,
                ...conditionalStyle
            }
        }
    ]);

    if (cell.isGrouped) {
        const toggleProps = row.getToggleRowExpandedProps();

        const handleClick = () => {
            // @ts-ignore
            toggleProps?.onClick();
        };

        return (
            <Table.GroupByHeader
                {...toggleProps}
                handleEditRow={
                    isShowEdit && RoleService.hasCreateRuleAccess
                        ? () => handleEditRow && handleEditRow(cell)
                        : undefined
                }
                title={cell.render('Cell')}
                dataTestId={`group-by-${cell.column?.id}`}
                onClick={handleOnClickEvent(handleClick)}
                onKeyDown={handleKeyEvent(handleClick)}
                containerClassName={clsx(tdClassName, styles.groupBy)}
                iconName={!row.isExpanded ? 'arrow-down-1' : 'arrow-up-1'}
                count={NumberService.getLocaleNumber(row.subRows.length)}
            />
        );
    }

    if (cell.isAggregated) {
        return <>{cell.render('Aggregated')}</>;
    }

    if (cell.isPlaceholder) {
        return null;
    }

    // map the return object from the function to the styles we have in our css.
    const conditionalClasses = conditionalRowClasses?.(row, styles as ConditionalRowClassNames);

    return (
        <div
            data-testid={`row-cell-${cell.column.id}`}
            className={clsx(tdClassName, styles.cellBody, conditionalClasses)}
            {...cellProps}
        >
            <TableCell cell={cell} />
        </div>
    );
}

export default RowCell;
